import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Tracks the most recent in-portal pathname so back navigation can return
 * users to where they actually came from (e.g. Account → Top-up → back → Account)
 * instead of always falling back to a hard-coded parent route.
 */
const STORAGE_KEY = "elefa:portalNavStack";
const MAX = 10;

function readStack(): string[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeStack(stack: string[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack.slice(-MAX)));
  } catch {
    /* ignore */
  }
}

/** Returns the previous portal pathname (the one before the current page). */
export function getPreviousPortalPath(currentPath: string): string | null {
  const stack = readStack();
  // Walk backwards skipping the current path
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i] !== currentPath && stack[i].startsWith("/connect-portal/")) {
      return stack[i];
    }
  }
  return null;
}

/** Mount once at portal layout level - records every portal navigation. */
export function usePortalNavHistory() {
  const { pathname } = useLocation();
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname.startsWith("/connect-portal/")) return;
    if (lastRef.current === pathname) return;
    lastRef.current = pathname;
    const stack = readStack();
    if (stack[stack.length - 1] !== pathname) {
      stack.push(pathname);
      writeStack(stack);
    }
  }, [pathname]);
}
